import React, { useEffect, useMemo, useState } from 'react';
import styles from './BulkBindInstruction.module.css';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

import BindMatrix from '../BindMatrix/BindMatrix';
import useApi from '../../hooks/useApi.hook';
import useFillSelect from '../../hooks/useFillSelect.hook';
import useProfessionRows from '../../hooks/useProfessionRows.hook';
import { getAllRulesUrl, createRulesManyUrl, deleteRulesManyUrl } from '../../helpers/constants';


export default function BulkBindInstruction({ professions, instructions }) {
	const api = useApi();
	const [loading, setLoading] = useState(false);
	const [saveOk, setSaveOk] = useState(false);
	const [resetOk, setResetOk] = useState(false);
	const [errorApi, setErrorApi] = useState(undefined);
	const [currentState, setCurrentState] = useState(() => new Set());
	const [isDirty, setIsDirty] = useState(false);

	const {
		itemDict: rulesDict,
		getItems: getRules,
		loading: rulesLoading
	} = useFillSelect({ endpoint: getAllRulesUrl, labelField: 'id' });

	const rules = useMemo(() => Object.values(rulesDict), [rulesDict]);

	const baseState = useMemo(() => {
		const s = new Set();
		rules.forEach(rule => s.add(`${rule.profession_id}-${rule.instruction_id}`));
		return s;
	}, [rules]);


	const rowItems = useProfessionRows(professions);
	const colItems = useMemo(
		() => [...instructions]
			.sort((a, b) => a.title.localeCompare(b.title))
			.map(i => ({ id: i.id, label: i.title })),
		[instructions]
	);

	useEffect(() => {
		setCurrentState(new Set(baseState));
		setIsDirty(false);
	}, [baseState]);

	const toggleAll = (insId) => {
		const allChecked = rowItems.every(row => currentState.has(`${row.id}-${insId}`));
		setCurrentState(prev => {
			const next = new Set(prev);
			rowItems.forEach(row => {
				const key = `${row.id}-${insId}`;
				if (allChecked) next.delete(key);
				else next.add(key);
			});
			return next;
		});
		setIsDirty(true);
	};

	const toggleCell = (profId, insId) => {
		const key = `${profId}-${insId}`;
		setCurrentState(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
		setIsDirty(true);
	};

	const resetChanges = () => {
		setCurrentState(new Set(baseState));
		setIsDirty(false);
		setResetOk(true);
		setTimeout(() => setResetOk(false), 1500);
	};

	const groupByInstruction = (pairs) => {
		const map = {};
		pairs.forEach(key => {
			const [profId, insId] = key.split('-').map(Number);
			if (!map[insId]) map[insId] = [];
			map[insId].push(profId);
		});
		return map;
	};

	const saveChanges = async () => {
		const toAdd = [...currentState].filter(k => !baseState.has(k));
		const toRemove = [...baseState].filter(k => !currentState.has(k));

		const addGroups = groupByInstruction(toAdd);
		const removeGroups = groupByInstruction(toRemove);

		setLoading(true);
		setErrorApi(undefined);
		try {
			const addRequests = Object.entries(addGroups).map(([insId, profIds]) =>
				api.post(createRulesManyUrl, {
					instruction_ids: [Number(insId)],
					profession_ids: profIds,
					bind_to_all: false
				})
			);
			const removeRequests = Object.entries(removeGroups).map(([insId, profIds]) =>
				api.delete(deleteRulesManyUrl, {
					data: {
						instruction_ids: [Number(insId)],
						profession_ids: profIds
					}
				})
			);
			await Promise.all([...addRequests, ...removeRequests]);
			await getRules();
			setSaveOk(true);
			setTimeout(() => setSaveOk(false), 1500);
		} catch (e) {
			setErrorApi(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
			await getRules();
		} finally {
			setLoading(false);
		}
	};

	if (rulesLoading) {
		return <div className={styles.loadingWrap}><Spinner /></div>;
	}

	return (
		<div className={styles.container}>
			<ErrorMessage error={errorApi} onClose={() => setErrorApi(undefined)} />
			<BindMatrix
				loading={loading}
				rows={rowItems}
				columns={colItems}
				currentState={currentState}
				baseState={baseState}
				toggleCell={toggleCell}
				toggleAll={toggleAll}
			/>
			<div className={styles.actions}>
				<Button
					onClick={saveChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${saveOk ? styles.btnOk : ''}`}
				>
					{saveOk ? '✓ Сохранено' : 'Сохранить'}
				</Button>
				<Button
					onClick={resetChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${resetOk ? styles.btnOk : ''}`}
				>
					{resetOk ? '✓ Сброшено' : 'Сбросить'}
				</Button>
			</div>
		</div>
	);
}